import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  Paid as PaidIcon,
  Approval as ApprovalIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { expenseService } from '../../services/expenseService';
import { exportToExcel } from '../../utils/excelExport';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
dayjs.locale('es');

const ExpenseList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [expenses, setExpenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({
    total_amount: 0,
    total_count: 0,
    pending_amount: 0,
    pending_count: 0,
    approved_amount: 0,
    approved_count: 0
  });
  
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    start_date: '',
    end_date: '',
    search: ''
  });
  
  const [selectedExpense, setSelectedExpense] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);

  useEffect(() => {
    loadExpenses();
    loadCategories();
  }, [paginationModel, filters]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...filters
      };
      
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await expenseService.getAll(params);
      
      setExpenses(response.data.expenses?.data || []);
      setTotal(response.data.expenses?.total || 0);
      setTotals(response.data.totals || {
        total_amount: 0,
        total_count: 0,
        pending_amount: 0,
        pending_count: 0,
        approved_amount: 0,
        approved_count: 0
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await expenseService.getCategories();
      setCategories(response.data);
    } catch (err) {
      console.error('Error loading categories:', err);
      setCategories({
        'Salarios': 'Pagos a profesores y personal',
        'Materiales': 'Materiales educativos y de oficina',
        'Servicios': 'Servicios públicos, internet, etc.',
        'Mantenimiento': 'Mantenimiento de equipos e instalaciones',
        'Marketing': 'Publicidad y promoción',
        'Eventos': 'Eventos escolares y actividades',
        'Tecnología': 'Equipos y software',
        'Otros': 'Otros gastos'
      });
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedExpense) return;
    
    try {
      await expenseService.delete(selectedExpense.id);
      loadExpenses();
      setDeleteDialogOpen(false);
      setSelectedExpense(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar gasto');
    }
  };

  const handleApprove = async () => {
    if (!selectedExpense) return;
    
    try {
      await expenseService.approve(selectedExpense.id);
      loadExpenses();
      setApproveDialogOpen(false);
      setSelectedExpense(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al aprobar gasto');
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedExpense) return;
    
    try {
      await expenseService.markAsPaid(selectedExpense.id);
      loadExpenses();
      setMarkPaidDialogOpen(false);
      setSelectedExpense(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al marcar como pagado');
    }
  };

  const handleExport = () => {
    const data = expenses.map(e => ({
      'ID': e.id,
      'Concepto': e.concept,
      'Descripción': e.description || '',
      'Monto': e.amount,
      'Fecha': dayjs(e.expense_date).format('DD/MM/YYYY'),
      'Categoría': e.category || '',
      'Proveedor': e.supplier || '',
      'Factura': e.invoice_number || '',
      'Estado': e.status === 'paid' ? 'Pagado' : 
                e.status === 'approved' ? 'Aprobado' : 
                e.status === 'pending' ? 'Pendiente' : 'Cancelado'
    }));
    
    exportToExcel(data, 'gastos');
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'paid':
        return <Chip icon={<PaidIcon />} label="Pagado" color="success" size="small" />;
      case 'approved':
        return <Chip icon={<ApprovalIcon />} label="Aprobado" color="info" size="small" />;
      case 'pending':
        return <Chip icon={<ErrorIcon />} label="Pendiente" color="warning" size="small" />;
      case 'cancelled':
        return <Chip icon={<WarningIcon />} label="Cancelado" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'RUB') => {
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₽';
    return `${symbol} ${amount.toLocaleString()}`;
  };

  const categoryOptions = Object.keys(categories).map(cat => ({
    value: cat,
    label: cat
  }));

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'concept',
      headerName: 'Concepto',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'category',
      headerName: 'Categoría',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Sin categoría'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'amount',
      headerName: 'Monto',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="error.main">
          {formatCurrency(params.value, params.row.currency)}
        </Typography>
      )
    },
    {
      field: 'expense_date',
      headerName: 'Fecha',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {dayjs(params.value).format('DD/MM/YYYY')}
        </Typography>
      )
    },
    {
      field: 'supplier',
      headerName: 'Proveedor',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/expenses/${params.row.id}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => navigate(`/expenses/${params.row.id}/edit`)}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'pending' && (
            <Tooltip title="Aprobar gasto">
              <IconButton
                size="small"
                color="info"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedExpense(params.row);
                  setApproveDialogOpen(true);
                }}
              >
                <ApprovalIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {(params.row.status === 'approved' || params.row.status === 'pending') && (
            <Tooltip title="Marcar como pagado">
              <IconButton
                size="small"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedExpense(params.row);
                  setMarkPaidDialogOpen(true);
                }}
              >
                <PaidIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedExpense(params.row);
                setDeleteDialogOpen(true);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gastos
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ mr: 1 }}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/expenses/new')}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Nuevo Gasto
          </Button>
        </Box>
      </Box>

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Total Gastado</Typography>
              <Typography variant="h4">
                {formatCurrency(totals.total_amount)}
              </Typography>
              <Typography variant="caption">{totals.total_count} gastos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Pendientes</Typography>
              <Typography variant="h4">
                {formatCurrency(totals.pending_amount)}
              </Typography>
              <Typography variant="caption">{totals.pending_count} gastos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Typography variant="body2">Aprobados</Typography>
              <Typography variant="h4">
                {formatCurrency(totals.approved_amount)}
              </Typography>
              <Typography variant="caption">{totals.approved_count} gastos</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por concepto, proveedor..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoría</InputLabel>
              <Select
                value={filters.category}
                label="Categoría"
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                disabled={loadingCategories}
              >
                <MenuItem value="">Todas</MenuItem>
                {categoryOptions.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Inicio"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.start_date}
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Fin"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.end_date}
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.status}
                label="Estado"
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="paid">Pagado</MenuItem>
                <MenuItem value="approved">Aprobado</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={() => loadExpenses()}
                fullWidth
                size="small"
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({ status: '', category: '', start_date: '', end_date: '', search: '' });
                }}
                size="small"
              >
                <RefreshIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={expenses}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) => navigate(`/expenses/${params.id}`)}
          disableRowSelectionOnClick
          localeText={{
            noRowsLabel: 'No hay gastos registrados'
          }}
        />
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de eliminar este gasto?
          </Typography>
          {selectedExpense && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Concepto:</strong> {selectedExpense.concept}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> {formatCurrency(selectedExpense.amount, selectedExpense.currency)}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha:</strong> {dayjs(selectedExpense.expense_date).format('DD/MM/YYYY')}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Aprobar Gasto</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Aprobar este gasto?
          </Typography>
          {selectedExpense && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Concepto:</strong> {selectedExpense.concept}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> {formatCurrency(selectedExpense.amount, selectedExpense.currency)}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleApprove} color="info" variant="contained">
            Aprobar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog open={markPaidDialogOpen} onClose={() => setMarkPaidDialogOpen(false)}>
        <DialogTitle>Marcar como Pagado</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Marcar este gasto como pagado?
          </Typography>
          {selectedExpense && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Concepto:</strong> {selectedExpense.concept}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> {formatCurrency(selectedExpense.amount, selectedExpense.currency)}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleMarkAsPaid} color="success" variant="contained">
            Marcar como Pagado
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseList;